import { useCallback, useMemo } from 'react';
import { IconButton } from '@Components/IconButton';
import { Dropdown } from '@Components/Dropdown';
import { MdClose } from 'react-icons/md';
import { Placement, Size } from '@Components/designConstants';
import {
  CLIENT_TIME_FIELDS,
  ClientTimeFieldKey,
  CpsClientSearchResult,
  CpsClientTimeLogForm
} from './cps-utils';
import { ClientSearchCombobox } from './ClientSearchCombobox';
import { TimeInput } from './TimeInput';

interface SurveyLineItemProps {
  index: number;
  log: CpsClientTimeLogForm;
  onClientChange: (index: number, client: CpsClientSearchResult | null) => void;
  onRelationshipChange: (index: number, relationshipId: string) => void;
  onTimeChange: (index: number, field: ClientTimeFieldKey, value: number) => void;
  onDelete: (index: number) => void;
}

const SurveyLineItem = ({
  index,
  log,
  onClientChange,
  onRelationshipChange,
  onTimeChange,
  onDelete
}: SurveyLineItemProps) => {
  const relationshipOptions = useMemo(() => {
    if (!log.client) return [];
    return (log.client.relationships ?? []).map(rel => ({
      value: rel.id,
      label: rel.name
    }));
  }, [log.client]);

  const handleClientChange = useCallback(
    (client: CpsClientSearchResult | null) => onClientChange(index, client),
    [index, onClientChange]
  );

  const handleRelationshipChange = useCallback(
    (id: string) => onRelationshipChange(index, id),
    [index, onRelationshipChange]
  );

  const handleDelete = useCallback(() => onDelete(index), [index, onDelete]);

  return (
    <div className='cps-survey-line-item'>
      <div className='cps-line-cell cps-cell-client'>
        <ClientSearchCombobox value={log.client} onChange={handleClientChange} />
      </div>
      <div className='cps-line-cell cps-cell-relationship'>
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={Size.LG}
          options={relationshipOptions}
          value={log.relationship?.id ?? ''}
          onChange={handleRelationshipChange}
          placeholder='Select...'
          disabled={!log.client || relationshipOptions.length === 0}
        />
      </div>
      {CLIENT_TIME_FIELDS.map(field => (
        <div key={field} className='cps-line-cell cps-cell-time'>
          <TimeInput value={log[field]} onChange={val => onTimeChange(index, field, val)} />
        </div>
      ))}
      <div className='cps-line-cell cps-cell-total'>
        <TimeInput value={log.totalHours} readOnly isTotal />
      </div>
      <div className='cps-line-cell cps-cell-actions'>
        <IconButton onClick={handleDelete} aria-label='Remove line item'>
          <MdClose />
        </IconButton>
      </div>
    </div>
  );
};

export { SurveyLineItem };
